/**
 * **climax テーマの盤**（Issue #543 → #565。索引 #539）。**ギルティギア Xrd の鋼の床**（ユーザー 2026-09-28）。
 *
 * - 地（`matBack`）… 刻印の入ったガンメタルの床板の**マットの絵**（`field/mat_*.webp`）。
 *   **固定の画素で貼らない**: Field の平面（`field.plane`）に合わせて描くたびに大きさを決め、縦横比が合わない分は
 *   覆って切り抜く（`coverRect`）。**マットの中央の境目を平面の中央（P1 と P2 の境）に合わせる**
 * - 枠線（`matFront`）… 各ゾーンの矩形（`field.zonePlane`）に**鋼の角の括弧の枠の絵**（`field/frame_*.webp`）を
 *   **9 分割で**伸び縮みさせて描く（角の飾りは潰さない）。枠はゾーンの種類ごとに選べる
 *   （モンスター / 魔法・罠 / フィールド魔法 / その他）。手札の場所には描かない
 * - 舞台の背景（テーマの `background`）… `field/stage_*.webp` から選ぶ（既定は B）。`index.js`
 *
 * 実写（`live`）では地の濃さが 0 なので（`core/theme` の `FIELD_LAYERS` の既定）、**枠の絵だけが実写に重なる**。
 * 描くのは**平面の紙**（`stage/FieldLayer.js`）なので、実写のゾーンが台形に写るのはカメラの仕事（ここは矩形に描くだけ）。
 *
 * 画像はページが先に読む（テーマの `images`）。読めていない間は描かずに飛ばし、読めたら紙が描き直される
 * （`scene.imagesReady`）。数値と選択は全部 `FIELD_STYLE` にある（GUI で詰める）。
 */
import { withDefaults } from '../../core/theme';
import { coverRect, drawNineSlice, fitText, inQuad, quadPath, rgba } from '../../canvas/paint';
import { imageReady, themeImage } from '../../canvas/images';
import { videoPlaneRect } from '../../core/field';
import { parseZoneKey } from '../../core/field/zones';
import { FONT } from './fx/common';
import {
  FRAME_KINDS, FRAME_SIZE, FRAME_SLICE, FRAMES, MATS, STAGE_BACKGROUNDS,
} from './field/assets';

const NONE = Object.freeze(['なし', 'none']);
const choices = (table) => Object.freeze([...Object.entries(table).map(([k, v]) => Object.freeze([v.label, k])), NONE]);

export const FIELD_SCHEMA = Object.freeze({
  stageBg: { type: 'select', label: '舞台の背景', options: choices(STAGE_BACKGROUNDS), part: 'background' },
  stageColor: { type: 'color', label: '舞台の地の色（絵の後ろ）', part: 'background' },
  stageBright: { type: 'number', label: '背景の明るさ（1 = そのまま）', min: 0, max: 1.5, step: 0.05, part: 'background' },
  stageSat: { type: 'number', label: '背景の彩度（1 = そのまま）', min: 0, max: 1.5, step: 0.05, part: 'background' },
  matImage: { type: 'select', label: 'マットの絵', options: choices(MATS), part: 'field:matBack' },
  matColor: { type: 'color', label: 'マットの地の色（絵の後ろ）', part: 'field:matBack' },
  matShade: { type: 'number', label: 'マットを沈める（黒の重ね）', min: 0, max: 0.8, step: 0.01, part: 'field:matBack' },
  zoneWellA: { type: 'number', label: 'ゾーンの窪み（黒の濃さ）', min: 0, max: 0.9, step: 0.01, part: 'field:matBack' },
  frameMonster: { type: 'select', label: '枠: モンスター', options: choices(FRAMES), part: 'field:matFront' },
  frameSpelltrap: { type: 'select', label: '枠: 魔法・罠', options: choices(FRAMES), part: 'field:matFront' },
  frameField: { type: 'select', label: '枠: フィールド魔法', options: choices(FRAMES), part: 'field:matFront' },
  frameOther: { type: 'select', label: '枠: その他（墓地・除外・デッキ・EX）', options: choices(FRAMES), part: 'field:matFront' },
  frameOut: { type: 'number', label: '枠をゾーンの外へ広げる（比）', min: 0, max: 0.4, step: 0.01, part: 'field:matFront' },
  frameCorner: { type: 'number', label: '枠の角の大きさ（倍）', min: 0.4, max: 2, step: 0.05, part: 'field:matFront' },
  labelA: { type: 'number', label: '1 枚ぶんの場所の文字の濃さ', min: 0, max: 1, step: 0.05, part: 'field:matFront' },
});

/** 盤の層に足す飾りは無い（枠は絵。光る線はやめた。Issue #565） */
export const FIELD_PARTS = Object.freeze([]);

/** 既定値（**ここが唯一の真実**）。割り当てはユーザー 2026-09-28 */
export const FIELD_STYLE = Object.freeze({
  stageBg: 'b', stageColor: '#0b0d10', stageBright: 1, stageSat: 1,
  matImage: 'playmat', matColor: '#1a1b1f', matShade: 0, zoneWellA: 0.15,
  frameMonster: 'monster', frameSpelltrap: 'spelltrap', frameField: 'spelltrap', frameOther: 'other',
  frameOut: 0.05, frameCorner: 1, labelA: 0.7,
});

const withField = withDefaults(() => [FIELD_STYLE]);
const fieldStyle = (cfg) => withField(cfg?.style);

/**
 * **舞台の背景**（テーマの `background`。Issue #565）。`stageBg` の選択 → 絵、`none` なら地の色だけ
 * @param style `resolveStyle` を通した見た目の値
 */
export function stageBackground(style) {
  const v = withField(style);
  const bg = STAGE_BACKGROUNDS[v.stageBg];
  return { image: bg?.src ?? null, color: v.stageColor, brightness: v.stageBright, saturate: v.stageSat };
}

/**
 * **マットをどこに描くか**（純関数。テストが Field ごとの大きさの違いを見る）。
 * 平面いっぱいを覆い、絵の中央の境目（`bandY`）を平面の中央に合わせる
 */
export function matPlacement(field, mat) {
  const { w, h } = field.plane;
  return coverRect(mat.size, { x: 0, y: 0, w, h }, { x: 0.5, y: mat.bandY });
}

/** 地。マットの絵を平面に合わせて敷く（絵が無い・読めていない間は地の色だけ） */
export function drawMatBackground(ctx, scene, cfg) {
  const v = fieldStyle(cfg);
  const { w, h } = scene.field.plane;
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, w, h);
  ctx.clip();
  ctx.fillStyle = v.matColor;
  ctx.fillRect(0, 0, w, h);
  const mat = MATS[v.matImage];
  const im = mat ? themeImage(mat.src) : null;
  if (mat && imageReady(im)) {
    const r = matPlacement(scene.field, mat);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(im, r.x, r.y, r.w, r.h);
  }
  if (v.matShade > 0) {
    ctx.fillStyle = rgba('#000000', v.matShade);
    ctx.fillRect(0, 0, w, h);
  }
  ctx.restore();
  /*
   * **ゾーンの窪み**（マットに刷った暗い区画）。鋼の枠は同じ鋼のマットの上だと沈むので、枠の中を暗くして区画を立たせる。
   * 地の層に描くので実写（地の濃さ 0）では出ない＝実写のマットは暗くならない。
   */
  if (!(v.zoneWellA > 0)) return;
  ctx.fillStyle = rgba('#000000', v.zoneWellA);
  scene.field.keys.forEach((key) => {
    const at = parseZoneKey(key);
    const quad = scene.field.zonePlane(key);
    if (!at || !quad || !frameFor(at.kind, v)) return;
    quadPath(ctx, quad);
    ctx.fill();
  });
}

/** 実写動画。**平面に置いて平面で切る**（上下の手札の帯は平面の外に落ちる） */
export function drawLiveField(ctx, scene) {
  const el = scene.video?.el;
  const size = scene.video?.size;
  if (!el || !(size?.w > 0) || !(size?.h > 0)) return;
  if (typeof el.readyState === 'number' && el.readyState < 2) return;
  const { w, h } = scene.field.plane;
  // 要素が元の映像の一部だけ（描く側は平面に入る部分だけ渡す。Issue #808）でも、置く場所は元の映像の座標で決まる
  const r = videoPlaneRect(scene.video, scene.field.plane);
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, w, h);
  ctx.clip();
  ctx.drawImage(el, r.x, r.y, r.w, r.h);
  ctx.restore();
}

/** 1 枚ぶんの場所に刷る文字 */
const ZONE_LABELS = Object.freeze({
  field: 'FIELD', maindeck: 'DECK', grave: 'GRAVE', removed: 'BANISH', extra: 'EXTRA',
});

/** そのゾーンの種類に選ばれている枠（`none` なら null） */
export function frameFor(kind, style) {
  const v = withField(style);
  const key = FRAME_KINDS[kind];
  return key ? (FRAMES[v[key]] ?? null) : null;
}

/**
 * **枠をどこに描くか**（ゾーンの矩形の中の座標。純関数）。ゾーンの短い辺の `frameOut` 倍だけ外へ広げ、
 * 角は絵の縦横比のまま（9 分割。`nineSliceRects`）
 * @returns {{ box: {x: number, y: number, w: number, h: number}, scale: number }}
 */
export function framePlacement(w, h, style) {
  const v = withField(style);
  const o = Math.min(w, h) * v.frameOut;
  const box = { x: -o, y: -o, w: w + o * 2, h: h + o * 2 };
  const scale = Math.min(box.w / FRAME_SIZE.w, box.h / FRAME_SIZE.h) * v.frameCorner;
  return { box, scale };
}

/**
 * 区画。各ゾーンの矩形に**その種類の枠の絵**を 9 分割で描く（枠の中は透明なので、実写ではカードが透けて見える）。
 * 1 枚ぶんの場所は行き先の文字（銀＋黒の縁）。
 */
export function drawZones(ctx, scene, cfg) {
  const v = fieldStyle(cfg);
  const { field } = scene;
  field.keys.forEach((key) => {
    const quad = field.zonePlane(key);
    const at = parseZoneKey(key);
    if (!quad || !at) return;
    const frame = frameFor(at.kind, v);
    const label = ZONE_LABELS[at.kind];
    if (!frame && !label) return;
    inQuad(ctx, quad, (w, h) => {
      const im = frame ? themeImage(frame.src) : null;
      if (imageReady(im)) {
        const { box, scale } = framePlacement(w, h, v);
        ctx.imageSmoothingQuality = 'high';
        drawNineSlice(ctx, im, FRAME_SLICE, box, scale);
      }
      if (!label || !(v.labelA > 0)) return;
      ctx.translate(w / 2, h / 2);
      if (field.side(at.player) === 'top') ctx.rotate(Math.PI);
      ctx.globalAlpha *= v.labelA;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const size = Math.max(14, Math.min(w, h) * 0.2);
      ctx.font = `900 ${size}px ${FONT}`;
      const text = fitText(ctx, label, w * 0.8, size);
      ctx.lineJoin = 'round';
      ctx.lineWidth = Math.max(2, size * 0.16);
      ctx.strokeStyle = '#050506';
      ctx.strokeText(text, 0, 0);
      ctx.fillStyle = '#d9dde4';
      ctx.fillText(text, 0, 0);
    });
  });
}

/** ゾーンの枠とキー（位置合わせの確認用。既定は濃さ 0） */
export function drawZoneLines(ctx, scene, config = {}) {
  const { field } = scene;
  field.keys.forEach((key) => {
    const quad = field.zonePlane(key);
    if (!quad) return;
    quadPath(ctx, quad);
    ctx.strokeStyle = '#7ae8ff';
    ctx.lineWidth = 3;
    ctx.stroke();
    if (!config?.zoneLabels) return;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.font = `20px ${FONT}`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(key, quad[0][0] + 4, quad[0][1] + 22);
  });
}

export default Object.freeze({
  matBack: drawMatBackground,
  video: drawLiveField,
  matFront: drawZones,
  lines: drawZoneLines,
});
